import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import { easeInOut } from '@config/animations.config';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IProductVariant } from '@models/IProductVariant';
import Tags from '@molecules/Tags/Tags';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props extends IProductVariant {
  readonly productListingName?: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly isLightMode?: boolean;
}

const KitCard = ({
  buyNowLink,
  fromPriceLabel,
  hidePriceFromSavePill,
  kitNewLabel,
  kitOnlyPriceLabel,
  newProductLabel,
  productListingName,
  pricingData,
  savePriceLabel,
  shippingLabel,
  statusLabels,
  thumbnail,
  title,
  trackingData,
  vatLabel,
  isLightMode,
}: Props) => {
  const [isHovered, setHover] = useState(false);
  const isXLarge = useMediaQuery(1440);
  const [ctaBoxHeight, setCtaBoxHeight] = useState(0);
  const ctaBox = useRef<HTMLDivElement>(null);
  const trackingDataJson = JSON.stringify(trackingData ?? '');

  useEffect(() => {
    setCtaBoxHeight(ctaBox.current?.clientHeight || 0);
  }, []);

  const contVariants = {
    hover: { y: -ctaBoxHeight },
    initial: { y: 0 },
  };

  const titleVariants = {
    hover: { y: -49 },
    initial: { y: 0 },
  };

  const ctaVariants = {
    hover: { opacity: 1 },
    initial: { opacity: isXLarge ? 1 : 0 },
  };

  const imgVariants = {
    hover: { y: -15, scale: 0.9 },
    inActive: { y: 0, scale: 1 },
  };

  const handleSelectItemEvent = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'selectItem',
      ecommerce: {
        currency: trackingData.currency,
        items: [
          mapToNewTrackingData(trackingData, {
            listingName: productListingName,
          }),
        ],
      },
    });
  };

  return (
    <div
      track-product-impression="true"
      data-product={trackingDataJson}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={classNames(
        'pointer-events-auto z-0 h-full w-full gap-5 overflow-hidden rounded-[10px] p-5',
        isLightMode ? 'bg-white text-black-100' : 'bg-black-100 text-white'
      )}
    >
      <div className="relative flex h-full w-full flex-col items-center justify-center">
        <div className="absolute top-0 left-0 z-20 flex w-full flex-row flex-wrap gap-4">
          <Tags
            hidePriceFromSavePill={hidePriceFromSavePill}
            newProductLabel={kitNewLabel || newProductLabel}
            pricingData={pricingData}
            savePriceLabel={savePriceLabel}
            statusLabels={statusLabels}
          />
        </div>

        <motion.div
          initial="initial"
          className="relative flex h-[240px] w-full shrink-0 items-center justify-center pt-10 xl:h-[280px]"
          animate={isHovered && !isXLarge ? 'hover' : 'initial'}
          variants={imgVariants}
          transition={easeInOut}
        >
          {thumbnail && (
            <a
              href={buyNowLink?.url || '/'}
              onClick={() => {
                if (buyNowLink?.url) handleSelectItemEvent();
              }}
              aria-label={title}
              className="flex h-full w-full"
            >
              <Image
                image={thumbnail}
                classes="z-10 w-full h-full xl:px-5"
                imageClasses="object-contain w-full h-full"
                sizes="(max-width: 768px) 376px, 500px"
              />
            </a>
          )}
        </motion.div>

        <motion.div
          initial="initial"
          animate={isHovered && !isXLarge ? 'hover' : 'initial'}
          variants={titleVariants}
          transition={easeInOut}
          className="relative mt-16 mb-[100px] flex w-full flex-col items-center justify-center px-10"
        >
          {title && <p className="h5 text-center">{title}</p>}
        </motion.div>

        <div className="mt-auto flex w-full flex-row items-end">
          <motion.div
            initial="initial"
            animate={isHovered && !isXLarge ? 'hover' : 'initial'}
            variants={contVariants}
            transition={easeInOut}
            className="relative flex grow flex-col items-start justify-center gap-3"
          >
            {pricingData?.formattedCalculatedPrice && (
              <PriceDisplayText
                pricingData={pricingData}
                fromPriceLabel={kitOnlyPriceLabel || fromPriceLabel}
                vatLabel={vatLabel}
                shippingLabel={shippingLabel}
                isLightMode={isLightMode}
              />
            )}

            <motion.div
              initial="initial"
              animate={isHovered && !isXLarge ? 'hover' : 'initial'}
              variants={ctaVariants}
              transition={{
                duration: 0.3,
              }}
              ref={ctaBox}
              className="flex w-full flex-row flex-wrap items-start justify-start gap-5 pt-5 xl:absolute xl:top-full xl:gap-[10px]"
            >
              {buyNowLink?.url && buyNowLink.name && (
                <Button
                  link={buyNowLink}
                  linkCallback={handleSelectItemEvent}
                />
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default KitCard;
